<template>
  <div class="container-fluid">
    <div class="card card-shadow" v-if="!settings">
      <h5 class="card-header text-center">
        <b-button
          variant="secondary"
          size="sm"
          @click="showPrevWeek"
          class="mr-1"
        >
          <i class="fas fa-caret-left"></i>
        </b-button>
        {{ weekStartDate }} - {{ weekEndDate }}
        <b-button
          variant="secondary"
          size="sm"
          @click="showNextWeek"
          class="ml-1"
        >
          <i class="fas fa-caret-right"></i>
        </b-button>
        <div class="float-right">
          <b-button
            variant="secondary"
            size="sm"
            title="Settings"
            @click="showSettings"
          >
            <i class="fas fa-cog"></i>
          </b-button>
        </div>
      </h5>
      <div class="card-body text-center">
        <div class="card-deck">
          <div
            class="card planner-card"
            v-for="(day, index) in week"
            :key="index"
          >
            <div class="card-body">
              <h5 class="card-title">{{ getDayOfWeek(day) }}</h5>
              <h6 class="card-subtitle mb-2 text-muted">
                {{ formatDate(day) }}
              </h6>
              <b-button
                variant="outline-primary"
                size="sm"
                title="Add Meal"
                @click="showAddMeal(day, index)"
              >
                <i class="fas fa-plus"></i> Add Meal
              </b-button>

              <b-modal
                :id="'modal_planner_add_meal_' + index"
                title="Add Meal"
                header-bg-variant="primary"
                header-text-variant="light"
                ok-title="Add Meal"
                ok-variant="primary"
                @ok="addPlannedMeal()"
              >
                <div class="form-group">
                  <label for="meal-date">Meal Date</label>
                  <b-form-datepicker
                    id="meal-date"
                    v-model="plannedMeal.date"
                    required
                  ></b-form-datepicker>
                  <span
                    class="glyphicon form-control-feedback"
                    aria-hidden="true"
                  ></span>
                  <div class="help-block with-errors"></div>
                </div>
                <div class="form-group">
                  <label for="meal-select">Meal</label>
                  <b-form-select
                    id="meal-select"
                    v-model="plannedMeal.meal"
                    :options="mealOptions"
                  ></b-form-select>
                </div>
                <div class="form-group">
                  <label for="selRecipe">Recipe</label>
                  <autocomplete
                    class="autocomplete-wrapper"
                    :search="searchRecipes"
                    :get-result-value="getRecipeValue"
                    placeholder="e.g. Pizza"
                    @change="updateRecipe"
                  ></autocomplete>
                </div>
              </b-modal>
            </div>
            <ul
              class="list-group list-group-flush"
              v-for="(meal, index) in meals"
              :key="index"
            >
              <li class="list-group-item list-group-item-primary text-center">
                <small class="text-muted">{{ meal.name }}</small>
              </li>
              <li class="list-group-item planner-list-item">
                <planner-meal
                  v-for="(planned, index) in planner.meals[
                    formatStandardDate(day)
                  ][meal.slug]"
                  :key="index"
                  :refkey="index"
                  :planned-meal="planned"
                  :meal-date="formatStandardDate(day)"
                  :meal="meal"
                ></planner-meal>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="card card-shadow" v-if="settings">
      <h5 class="card-header text-center">
        Settings
        <div class="float-right">
          <b-button variant="primary" size="sm" title="Settings">
            <i class="fas fa-cog"></i>
          </b-button>
        </div>
      </h5>
      <div class="card-body container">
        <div class="row settings-row">
          <div class="col">
            <h5>Manage Meals</h5>
            <small class="text-muted"
              >Add, rename, remove, or re-arrange your daily meals. Removing a
              meal will remove all planned recipes for that meal from your
              planner.</small
            >
            <table id="tblSettingsMeals" class="table table-striped mt-3">
              <thead>
                <tr>
                  <th></th>
                  <th>Name</th>
                  <th>Color</th>
                  <th></th>
                </tr>
              </thead>
              <tbody id="settings-meal-list">
                <tr
                  class="settings-meal-row"
                  v-for="(meal, index) in meals"
                  :key="index"
                >
                  <td class="handle meal-handle" style="border-bottom: 0">
                    <i class="fas fa-grip-vertical"></i>
                  </td>
                  <td>
                    <input
                      name="name"
                      type="text"
                      class="form-control meal-name"
                      placeholder="e.g. Breakfast"
                      v-model="meal.name"
                    />
                  </td>
                  <td>
                    <input
                      name="color"
                      type="color"
                      class="form-control meal-color"
                      placeholder="e.g. #52c7b8"
                      v-model="meal.color"
                    />
                  </td>
                  <td>
                    <b-button
                      variant="outline-success"
                      title="Save"
                      class="mx-1"
                      @click="saveMeal(meal)"
                    >
                      <i class="far fa-save"></i>
                    </b-button>
                    <b-button
                      variant="outline-danger"
                      title="Delete"
                      class="mx-1"
                      @click="deleteMeal(meal)"
                    >
                      <i class="far fa-trash-alt"></i>
                    </b-button>
                  </td>
                </tr>
              </tbody>
            </table>
            <b-button
              variant="outline-primary"
              size="sm"
              class="mt-0 mb-4"
              title="Add Meal"
              @click="addMeal"
            >
              <i class="fas fa-plus"></i> Add Meal
            </b-button>
          </div>
        </div>
        <div class="row settings-row">
          <div class="col">
            <h5>Automatic Shopping Lists</h5>
            <small class="text-muted"
              >Have ingredients for your upcoming recipes added to your shopping
              list automatically.</small
            >
            <ul class="list-group list-group-flush mt-3">
              <li
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                Add ingredients to my shopping list automatically:
                <b-form-checkbox
                  v-model="planner.auto_add"
                  switch
                  @change="savePlanner"
                ></b-form-checkbox>
              </li>
              <li
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                I typically shop on:
                <div class="form-group settings-form-group">
                  <b-form-select
                    v-model="family.shopping_day"
                    :options="days"
                    @change="saveFamily"
                  ></b-form-select>
                </div>
              </li>
              <li
                class="list-group-item d-flex justify-content-between align-items-center"
              >
                Add ingredients to my list this many days in advance:
                <div class="form-group settings-form-group">
                  <input
                    id="inpAdvanceDays"
                    name="advance_days"
                    type="number"
                    class="form-control"
                    placeholder="e.g. 1"
                    v-model="planner.advance_days"
                    @change="savePlanner"
                  />
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="card-footer text-muted">
        <b-link @click="hideSettings">
          <i class="fas fa-arrow-circle-left"></i> Back
        </b-link>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import FirebaseMixin from "@/mixins/Firebase";
import UtilsMixin from "@/mixins/Utils";
import { db } from "@/firebase";
import moment from "moment";
import Autocomplete from "@/components/Autocomplete";
import PlannerMeal from "@/components/PlannerMeal";
import slugify from "slugify";

export default {
  name: "Planner",
  mixins: [FirebaseMixin, UtilsMixin],
  components: { autocomplete: Autocomplete, "planner-meal": PlannerMeal },
  data() {
    return {
      ref: "planner",
      bind: [],
      planner: {
        meals: {},
        auto_add: false,
        advance_days: 1
      },
      family: {
        shopping_day: "sunday"
      },
      days: [
        { text: "Sunday", value: "sunday" },
        { text: "Monday", value: "monday" },
        { text: "Tuesday", value: "tuesday" },
        { text: "Wednesday", value: "wednesday" },
        { text: "Thursday", value: "thursday" },
        { text: "Friday", value: "friday" },
        { text: "Saturday", value: "saturday" }
      ],
      meals: [],
      default_meals: [],
      plannedMeal: {
        date: "",
        meal: {},
        recipe: {}
      },
      weeks: [],
      week: [],
      settings: false,
      placeholder: require("../../assets/recipe-placeholder.jpg"),
      public_recipes: [],
      family_recipes: [],
      resultLimit: 10,
      querySize: 100
    };
  },
  computed: {
    ...mapState(["isAuthenticated", "userProfile"]),
    ...mapGetters(["familyShoppingDay"]),
    familyKey() {
      return this.userProfile.family_key;
    },
    weekStartDate() {
      return this.formatDate(this.week[0]);
    },
    weekEndDate() {
      return this.formatDate(this.week[6]);
    },
    mealOptions() {
      return this.sourceOptions(this.meals);
    }
  },
  watch: {
    familyKey: {
      immediate: true,
      handler(family_key) {
        console.log(family_key);
        if (family_key !== null) {
          this.bindObject(
            "families/" + family_key,
            null,
            "family"
          ).then(() => {});
          this.bindObject(
            "planners/" + family_key,
            null,
            "planner"
          ).then(() => {});
          this.bindObject("meals/" + family_key, null, "meals").then(() => {
            if (this.meals.length === 0) {
              console.log(this.default_meals);
              this.default_meals.forEach(meal => {
                const newMeal = { ...meal };
                delete newMeal[".key"];
                console.log(newMeal);
                this.saveMeal(newMeal, true);
              });
            }
          });
        }
      }
    },
    planner: {
      immediate: true,
      handler(planner) {
        console.log(planner.meals);
        planner.meals = !planner.meals ? {} : planner.meals;
        this.week = this.getWeek();
      }
    },
    week: {
      immediate: true,
      handler(week) {
        week.forEach(day => {
          const date = this.formatStandardDate(day);
          this.planner.meals[date] = !this.planner.meals[date]
            ? {}
            : this.planner.meals[date];
          //this.$set(this.planner.meals, date, this.planner.meals[date]);
        });
      }
    }
  },
  methods: {
    showSettings() {
      this.settings = true;
    },
    hideSettings() {
      this.settings = false;
    },
    getWeek(inputDate = null) {
      inputDate = inputDate === null ? moment() : moment(inputDate);
      let week = new Array();
      let currentDay = inputDate.startOf("week");
      do {
        const loopDay = currentDay.clone();
        //loopDay.date = currentDay.clone();
        // loopDay.text = currentDay.format("dddd");
        // loopDay.textShort = currentDay.format("ddd");
        // loopDay.us_date = currentDay.format("L");
        // loopDay.iso_date = currentDay.format();
        week.push(loopDay);
        currentDay.add(1, "days");
      } while (week.length < 7);
      return week;
    },
    formatDate(date) {
      return moment(date).format("L");
    },
    formatStandardDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    getDayOfWeek(date) {
      return moment(date).format("dddd");
    },
    getDayOfWeekShort(date) {
      return moment(date).format("ddd");
    },
    prepareWeek(week, meals, calendarMeals) {
      week.forEach(day => {
        day.meals = new Array();
        meals.forEach(meal => {
          let m = new Object();
          m = { ...meal };
          m.planned = new Array();
          calendarMeals.forEach(cm => {
            cm.moment = moment(cm.meal_date);
            if (
              moment(cm.meal_date).isSame(day.iso_date, "day") &&
              meal.id === cm.meal.id
            ) {
              cm.recipe.image =
                cm.recipe.image !== null ? cm.recipe.image : this.placeholder;
              m.planned.push(cm);
            }
          });
          day.meals.push(m);
        });
      });
      return week;
    },
    showNextWeek() {
      const newWeekStart = moment(this.week[0]).add(1, "weeks");
      this.week = this.getWeek(newWeekStart);
    },
    showPrevWeek() {
      const newWeekStart = moment(this.week[0]).subtract(1, "weeks");
      this.week = this.getWeek(newWeekStart);
    },
    async searchRecipes(input) {
      if (input.length < 2) {
        return [];
      }
      const results = [];
      const names = [];
      let familyStartAt = null;
      let publicStartAt = null;
      let iterations = 0;
      // Search family recipes
      do {
        let count;
        console.log(
          "Family iteration: " +
            iterations +
            " Start at: " +
            familyStartAt +
            " Results: " +
            results.length
        );
        if (this.familyKey !== null) {
          if (familyStartAt === null) {
            await this.$rtdbBind(
              "family_recipes",
              db
                .ref(this.env + "families/" + this.familyKey + "/recipes")
                .orderByKey()
                .limitToFirst(this.querySize)
            );
          } else {
            await this.$rtdbBind(
              "family_recipes",
              db
                .ref(this.env + "families/" + this.familyKey + "/recipes")
                .orderByKey()
                .limitToFirst(this.querySize)
                .startAt(familyStartAt)
            );
          }
          count = this.family_recipes.length;
          this.family_recipes.forEach((recipe, index) => {
            if (
              recipe.name.toLowerCase().startsWith(input.toLowerCase()) &&
              !names.includes(recipe.name)
            ) {
              results.push({ ...recipe });
              names.push(recipe.name);
            }
            if (index === count - 1) {
              familyStartAt = recipe[".key"];
            }
          });
        }
        iterations++;
        if (count < this.querySize) {
          break;
        }
      } while (results.length < this.resultLimit && iterations < 5);
      // Search public recipes
      iterations = 0;
      while (results.length < this.resultLimit && iterations < 5) {
        console.log(
          "Public iteration: " +
            iterations +
            " Start at: " +
            publicStartAt +
            " Results: " +
            results.length
        );
        if (publicStartAt === null) {
          await this.$rtdbBind(
            "public_recipes",
            db
              .ref(this.env + "public_recipes")
              .orderByKey()
              .limitToFirst(this.querySize)
          );
        } else {
          await this.$rtdbBind(
            "public_recipes",
            db
              .ref(this.env + "public_recipes")
              .orderByKey()
              .limitToFirst(this.querySize)
              .startAt(publicStartAt)
          );
        }
        let count = this.family_recipes.length;
        this.public_recipes.forEach((recipe, index) => {
          if (
            recipe.name.toLowerCase().startsWith(input.toLowerCase()) &&
            !names.includes(recipe.name)
          ) {
            results.push({ ...recipe });
            names.push(recipe.name);
          }
          if (index === count - 1) {
            publicStartAt = recipe[".key"];
          }
        });
        iterations++;
        if (count < this.querySize) {
          break;
        }
      }
      return results;
    },
    getRecipeValue(result) {
      return result.name;
    },
    updateRecipe(value) {
      const recipe = { ...value };
      this.plannedMeal.recipe = recipe;
    },
    showAddMeal(day, index) {
      this.plannedMeal.meal = null;
      this.plannedMeal.recipe = null;
      this.plannedMeal.date = moment(day).format("YYYY-MM-DD");
      this.$bvModal.show("modal_planner_add_meal_" + index);
    },
    addPlannedMeal() {
      const planned = { ...this.plannedMeal };
      const meal = this.meals[this.getIndex(this.meals, planned.meal)];
      planned.meal = { ...meal };

      this.createObject(
        "planners/" +
          this.familyKey +
          "/meals/" +
          planned.date +
          "/" +
          meal.slug,
        planned
      ).catch(error => {
        console.log(error);
        window.toastr.error(
          "An error occurred while updating the planner. Please try again later."
        );
      });
    },
    getUniqueSlug(source, name) {
      let slug = slugify(name, { lower: true, strict: true });
      let modifier = 2;
      const slugs = [];
      source.forEach(item => {
        slugs.push(item.name);
      });
      while (slug in slugs) {
        modifier++;
        slug = slug + "-" + modifier;
      }
      return slug;
    },
    saveMeal(meal = null, hideAlerts = false) {
      if (meal === null) {
        meal = { ...this.meal };
      }
      console.log(meal);
      if (".key" in meal) {
        this.updateObject("meals/" + this.familyKey, meal[".key"], meal)
          .then(() => {
            if (!hideAlerts) {
              window.toastr.success("Meal saved successfully.");
            }
            // Update existing planned meals
            Object.values(this.planner.meals).forEach(day => {
              console.log(day);
              Object.values(day).forEach(slug => {
                console.log(slug);
                Object.values(slug).forEach(planned => {
                  console.log(planned);
                  if (meal.slug === planned.meal.slug) {
                    planned.meal = { ...meal };
                  }
                });
              });
            });
            this.savePlanner(true);
          })
          .catch(error => {
            console.log(error);
            if (!hideAlerts) {
              window.toastr.error(
                "An error occurred while saving the meal. Please try again later."
              );
            }
          });
      } else {
        meal.slug = this.getUniqueSlug(this.meals, meal.name);
        this.createObject("meals/" + this.familyKey, meal)
          .then(() => {
            if (!hideAlerts) {
              window.toastr.success("Meal saved successfully.");
            }
          })
          .catch(error => {
            console.log(error);
            if (!hideAlerts) {
              window.toastr.error(
                "An error occurred while saving the meal. Please try again later."
              );
            }
          });
      }
    },
    savePlanner(hideAlerts = false) {
      this.$nextTick().then(() => {
        const planner = { ...this.planner };
        console.log(planner);
        this.updateObject("planners/" + this.familyKey, null, planner)
          .then(() => {
            if (!hideAlerts) {
              window.toastr.success("Planner saved successfully.");
            }
          })
          .catch(error => {
            console.log(error);
            if (!hideAlerts) {
              window.toastr.error(
                "An error occurred while saving the planner. Please try again later."
              );
            }
          });
      });
    },
    saveFamily() {
      this.$nextTick().then(() => {
        const family = { ...this.family };
        console.log(family);
        this.updateObject("families/" + this.familyKey, null, family)
          .then(() => {
            window.toastr.success("Planner saved successfully.");
          })
          .catch(error => {
            console.log(error);
            window.toastr.error(
              "An error occurred while saving the planner. Please try again later."
            );
          });
      });
    },
    addMeal() {
      const meal = {
        name: "",
        color: "#52c7b8",
        slug: ""
      };
      this.createObject("meals/" + this.familyKey, meal).catch(error => {
        console.log(error);
        window.toastr.error(
          "An error occurred while creating the meal. Please try again later."
        );
      });
    },
    deleteMeal(meal) {
      console.log(meal);
      this.deleteObject("meals/" + this.familyKey, meal[".key"])
        .then(() => {
          // Remove existing planned meals
          const promises = [];
          Object.values(this.planner.meals).forEach(day => {
            console.log(day);
            Object.values(day).forEach(slug => {
              console.log(slug);
              if (slug === meal.slug) {
                promises.push(
                  this.deleteObject(
                    "planners/" + this.familyKey + "/meals/" + day + "/" + slug
                  )
                );
              }
            });
          });
          Promise.all(promises)
            .then(() => {
              window.toastr.success("The meal has been deleted successfully.");
            })
            .catch(error => {
              console.log(error);
              window.toastr.error(
                "An error occurred while deleting the meal. Please try again later."
              );
            });
        })
        .catch(error => {
          console.log(error);
          window.toastr.error(
            "An error occurred while deleting the meal. Please try again later."
          );
        });
    }
  },
  created() {
    this.bindObject("default_meals/", null, "default_meals").then(() => {
      if (this.meals.length === 0 && this.familyKey !== null) {
        this.default_meals.forEach(meal => {
          const newMeal = { ...meal };
          delete newMeal[".key"];
          console.log(newMeal);
          this.saveMeal(newMeal, true);
        });
      }
    });
  }
};
</script>

<style>
.planner-card {
  min-width: 150px;
  max-width: 300px;
  width: 100%;
  margin-left: auto !important;
  margin-right: auto !important;
  margin-bottom: 5px !important;
}

.planner-list-item {
  padding-left: 2px;
  padding-right: 2px;
  text-align: center;
  min-height: 150px;
}

.planned-meal {
  min-width: 130px;
  max-width: 280px;
  height: 40px;
  margin-left: 5px !important;
  margin-right: 5px !important;
  margin-bottom: 5px !important;
}

.planned-meal .card-img {
  height: 38px;
}

.planner-recipe-text {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  vertical-align: middle;
  font-size: 95%;
  font-weight: 400;
  color: white;
  padding-top: 7px;
  text-align: left;
  padding-left: 5px !important;
}

.planner-recipe-text > a {
  color: white;
}

.autocomplete-wrapper {
  width: 100%;
}

.autocomplete-input {
  width: 100%;
}

#tblSettingsMeals {
  border: 1px solid #dee2e6;
  margin-left: auto;
  margin-right: auto;
}

.settings-row {
  max-width: 650px;
  margin-left: auto;
  margin-right: auto;
}

.settings-form-group {
  max-width: 124px;
  margin-right: 0;
  margin-bottom: 0;
}

.meal-handle {
  width: 30px;
  height: 28px;
  float: left;
  cursor: pointer;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
